import React, {
  FC,
  useEffect,
  useState,
  useRef,
  KeyboardEventHandler,
} from "react";
import { connect } from "react-redux";
import {
  Dropdown,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  TextField,
  Text,
  mergeStyleSets,
} from "@fluentui/react";
import ToastContent from "../common/ToastContent";
import { toast } from "react-toastify";
import ApiSpinner from "../common/ApiSpinner";
import clsx from "clsx";
import { DailyReport, EmailReportsRequest } from "../../models";
import { ReportApi } from "../../api/reportApi";
import ReportItem from "./ReportItem";
import { pushNotification } from "../../api/pushNotification";
import * as GeneralStore from "../../store/GeneralStore";
import { State } from "../../store";
import "./Reports.css";
import Modal from "../common/Modal";
import CreatableSelect from "react-select/creatable";
import { Option } from "../../models/Option";
import {
  IPivotStyles,
  IStyleSet,
  Pivot,
  PivotItem,
  PivotLinkFormat,
  PivotLinkSize,
  mergeStyles,
} from "office-ui-fabric-react";
import Loader from "../common/Loader";
import { emailApi } from "../../api/emailApi";
import moment from "moment";
import TruckReportItem from "./TruckReportItem";

type Props = GeneralStore.GeneralState & typeof GeneralStore.actionCreators;
const pageSize = 15;

const classes = mergeStyleSets({
  dropDownDiv: {
    position: "absolute",
    top: "5px",
    right: "26%",
    width: "8%",
  },
  searchBoxDiv: {
    position: "absolute",
    top: "5px",
    right: "5px",
    width: "25%",
  },
  mainDiv: {
    overflow: "hidden",
    height: "100%",
  },
  mainList: {
    overflow: "auto",
    height: "78vh",
    //padding: "0 12px",
  },
  formContainer: { display: "flex", flexDirection: "column", height: "100%" },
  formHeader: {
    gap: 8,
    padding: 16,
    display: "flex",
    alignItems: "center",
    backgroundColor: "white",
  },
  toolbarSeparator: {
    height: "40px",
    width: "1px",
    background: "#D8DFE6",
    margin: "0",
  },
  printContainer: {
    flexGrow: 1,
    overflow: "auto",
    display: "flex",
  },
  sendButton: {
    padding: "8px 36px",
    backgroundColor: "#009FDF",
    color: "#F6F6F6",
    border: "none",
    borderRadius: 2,
  },
  errorButton: {
    padding: "8px 36px",
    backgroundColor: "red",
    color: "#F6F6F6",
    border: "none",
    borderRadius: 2,
  },
  viewButton: {
    width: "40px",
    height: "40px",
    borderRadius: "5px",
    border: "1px solid #E2E6E8",
    backgroundColor: "#FAFAFA",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    selectors: {
      ":hover": {
        backgroundColor: "#EEEEEE",
        textDecoration: "none",
      },
    },
  },
});
const buttonIcon = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 40,
  width: 40,
  justifyContent: "center",
  border: "1px solid #D8DFE6",
  borderRadius: "2px",
  display: "flex",
  alignItems: "center",
  color: "#4c4c4c",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0",
    },
  },
});

const pivotStyles: Partial<IStyleSet<IPivotStyles>> = {
  link: {
    minWidth: "120px",
    fontSize: "16px",
    selectors: {
      ":hover": {
        backgroundColor: "#EEF0F8",
        color: "#4C4C4C",
        fontWeight: 700,
      },
      ":active": {
        backgroundColor: "#EEF0F8",
        color: "#4C4C4C",
        fontWeight: 700,
      },
    },
  },
  linkIsSelected: {
    minWidth: "150px",
    backgroundColor: "#EEF0F8",
    color: "#4C4C4C",
    fontWeight: 700,
    fontSize: "16px",
    selectors: {
      ":hover": {
        backgroundColor: "#EEF0F8",
        color: "#4C4C4C",
        fontWeight: 700,
      },
      ":active": {
        backgroundColor: "#EEF0F8",
        color: "#4C4C4C",
        fontWeight: 700,
      },
    },
  },
};

const ReportPage: FC<Props> = (props: Props) => {
  const [dailyReports, setDailyReports] = useState<DailyReport[]>([]);
  const [nextPage, setNextPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isEndReached, setIsEndReached] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [keyword, setKeyword] = useState<string>();
  const [salesOrg, setSalesOrg] = useState<string>();
  const [selectedFileUrl, setSelectedFileUrl] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [pdfContentBlob, setPdfContentBlob] = useState<Blob | undefined>();
  const [inputValue, setInputValue] = useState<string>("");
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>();
  const [emailAddresses, setEmailAddresses] = useState<readonly Option[]>([]);
  const [emailStatus, setEmailStatus] = useState<string>("default");
  const [modality, setModality] = useState<number>(0);
  const [identifier, setIdentifier] = useState<string>("");

  const pageRef = useRef<HTMLIFrameElement>(null);

  const options: IDropdownOption[] = [
    { key: "ALL", text: "ALL" },
    { key: "PHL", text: "PHL" },
    { key: "USA", text: "USA" },
  ];

  const removeScrolling = () => {
    const main = document.getElementById("div-nomination-list");
    if (main) {
      main.removeEventListener("scroll", trackScrolling);
    }
  };

  const trackScrolling = async () => {
    const main = document.getElementById("main-scroll");
    if (main) {
      const isBottom =
        main.getBoundingClientRect().bottom <= window.innerHeight;
      if (!isBottom) {
        removeScrolling();
        setNextPage(nextPage + 1);
        // setNextPage((prev) => prev + 1);
      }
    }
  };

  const loadItemsAsync = async (
    pageNo: number,
    searchString: string,
    modality?: number
  ) => {
    try {
      if (pageNo === 1) {
        setDailyReports([]);
      }

      setErrorMessage(undefined);
      setIsLoading(true);

      const result =
        modality === 0
          ? await ReportApi.getDailyReportAsync(
              getSalesOrg(),
              pageNo,
              pageSize,
              searchString,
              0
            )
          : await ReportApi.getDailyReportNominationAsync(
              getSalesOrg(),
              pageNo,
              pageSize,
              searchString,
              modality
            );

      setIsLoading(false);
      setDailyReports(
        pageNo === 1 ? result.results : [...dailyReports, ...result.results]
      );

      const hasMorePages = Math.ceil(result.totalResults / pageSize) > pageNo;
      setIsEndReached(!hasMorePages);
      if (hasMorePages) {
        const wrappedElement = document.getElementById("div-nomination-list");
        if (wrappedElement)
          wrappedElement.addEventListener("scroll", trackScrolling);
      } else {
        removeScrolling();
      }
    } catch (err) {
      setIsEndReached(true);
      setIsLoading(false);
    }
  };

  const searchAsync = async () => {
    if (keyword !== undefined && (keyword.length > 2 || keyword.length === 0)) {
      await loadItemsAsync(1, keyword, modality);
    }
  };

  const getSalesOrg = () => {
    if (salesOrg) {
      return salesOrg;
    } else {
      return props.marineSalesOrg;
    }
  };

  const iconProps = { iconName: "Search" };

  useEffect(() => {
    const timeoutId = setTimeout(() => searchAsync(), 1000);

    return () => clearTimeout(timeoutId);
  }, [keyword]);

  useEffect(() => {
    if (getSalesOrg()) {
      loadItemsAsync(nextPage, keyword === undefined ? "" : keyword, modality);
    }
  }, [nextPage]);

  useEffect(() => {
    if (getSalesOrg()) {
      setNextPage(1);
      setDailyReports([]);
      loadItemsAsync(nextPage, keyword === undefined ? "" : keyword, modality);
    }
  }, [props.marineSalesOrg, salesOrg]);

  useEffect(() => {
    return removeScrolling;
  }, []);

  // useEffect(() => {
  //   props.setPageTitle("Reports");
  // }, []);

  useEffect(() => {
    pushNotification.connection.off("ReportUpdated");
    pushNotification.connection.off("UndoExecuted");
    pushNotification.connection.off("ReportAdded");

    pushNotification.connection.on("ReportUpdated", (message) => {
      var result = message as DailyReport;
      var lineHandlingNo = result.lineHandlingNo.toUpperCase();
      var dailyReportList = dailyReports;
      var lineHandling = dailyReportList.find(
        (l) => l.lineHandlingNo.toUpperCase() === lineHandlingNo
      );
      var cnt = 0;
      if (lineHandling !== undefined && cnt === 0) {
        dailyReportList.forEach((dailyReportItem) => {
          if (dailyReportItem.lineHandlingNo.toUpperCase() === lineHandlingNo) {
            dailyReportItem.departureDate = result.departureDate;
            dailyReportItem.comments = result.comments;
            dailyReportItem.lineHandlingActivities =
              result.lineHandlingActivities;

            dailyReportItem.nominations.forEach((nominationItem) => {
              var nomination = result.nominations.find(
                (n) =>
                  n.nominationNo.toUpperCase() ===
                  nominationItem.nominationNo.toUpperCase()
              );

              if (nomination !== undefined) {
                nominationItem.comments = nomination.comments;
              }
            });

            toast.info(
              <ToastContent
                title="Report Updated"
                content={`An update was made in Line Handling ${lineHandlingNo}.`}
                buttons={[{ text: "Dismiss", action: () => toast.dismiss() }]}
              />,
              {
                position: "bottom-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
              }
            );

            cnt += 1;
          }
        });

        setDailyReports(dailyReportList);
      }
    });

    pushNotification.connection.on("UndoExecuted", (message) => {
      var result = message as string[];
      var lineHandlingNo = result[0].toUpperCase();

      var dailyReportList = dailyReports;
      var lineHandling = dailyReportList.find(
        (l) => l.lineHandlingNo.toUpperCase() === lineHandlingNo
      );
      var cnt = 0;
      if (lineHandling !== undefined && cnt === 0) {
        setDailyReports(
          dailyReportList.filter(
            (lineHandlingItem) =>
              lineHandlingItem.lineHandlingNo !== lineHandlingNo
          )
        );

        toast.info(
          <ToastContent
            title="Report Removed"
            content={`An activity was undone in Line Handling ${lineHandlingNo}.`}
            buttons={[{ text: "Dismiss", action: () => toast.dismiss() }]}
          />,
          {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          }
        );
      }
    });

    pushNotification.connection.on("ReportAdded", (message) => {
      loadItemsAsync(1, keyword === undefined ? "" : keyword, modality);
    });
  }, [dailyReports]);

  const createOption = (label: string) => ({
    label,
    value: label,
  });

  const components = {
    DropdownIndicator: null,
  };

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case "Enter":
      case "Tab":
        if (!validateEmail(inputValue)) {
          setEmailErrorMessage("Invalid email address.");
          return;
        }
        if (emailAddresses === null) {
          const obj: readonly Option[] = [
            { value: inputValue, label: inputValue },
          ];
          setEmailAddresses(obj);
        } else {
          setEmailAddresses([...emailAddresses, createOption(inputValue)]);
        }

        setInputValue("");
        setEmailErrorMessage("");
        event.preventDefault();
    }
  };

  const onPrint = () => {
    const iframe = document.getElementById("print-window") as HTMLIFrameElement;

    if (iframe.contentWindow) {
      iframe.focus();
      iframe.contentWindow?.print();
    }
  };

  const blobToBase64 = (blob: any) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
    });
  };

  const closeModal = () => {
    const modal = document.getElementById("preview") as HTMLDialogElement;
    modal.close();
  };
  const SendEmail = async () => {
    if (emailAddresses.length === 0) {
      setEmailErrorMessage("Email address is required.");
      return;
    }

    setEmailStatus("sending");
    const currentDate = moment().format("YYYYMMDD-HHmm");

    const result = (await blobToBase64(pdfContentBlob)) as string;

    const array = result?.split(",");
    const base64 = array.length > 0 ? array[1] : "";

    const emailRequest = {
      recipients: emailAddresses.map((a: { value: string }) => a.value),
      attachments: [
        {
          content: base64,
          fileName: `${selectedFileName}.pdf`,
          mimeType: "application/pdf",
        },
      ],
      workpackets: [selectedFileName],
      identifier: identifier,
    } as EmailReportsRequest;
    let emailResponse;

    switch (modality) {
      case 0:
        emailResponse = await emailApi.sendEmailMarineReportsAsync(
          emailRequest
        );
        break;
      case 1:
        emailResponse = await emailApi.sendEmailTruckReportsAsync(emailRequest);
        break;
      case 2:
        emailResponse = await emailApi.sendEmailRailReportsAsync(emailRequest);
        break;
    }

    if (emailResponse.emailStatus) {
      setEmailStatus("success");

      setTimeout(function () {
        const modal = document.getElementById("preview") as HTMLDialogElement;
        modal.close();
        setEmailStatus("default");
        setEmailAddresses([]);
      }, 5000);
    } else {
      setEmailStatus("error");
      setTimeout(function () {
        setEmailStatus("default");
      }, 5000);
    }
  };

  const onDownload = () => {
    const anchor = document.createElement("a") as HTMLAnchorElement;
    anchor.href = selectedFileUrl;
    anchor.download = selectedFileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const IsNullOrEmpty = (string: string) => {
    if (string === undefined) return true;
    if (string === "") return true;
    if (string === null) return true;

    return false;
  };

  return (
    <>
      <div className={classes.mainDiv}>
        <div
          className="ms-Grid"
          dir="ltr"
          style={{ backgroundColor: "white", margin: "12px", padding: "0px" }}
        >
          <div className="ms-Grid-row" style={{ padding: "10px 20px" }}>
            <div className="ms-Grid-col ms-md4">
              <div style={{ fontSize: 24, lineHeight: "48px" }}>Reports</div>
            </div>
            <div className="ms-Grid-col ms-md2"></div>
            <div
              className="ms-Grid-col ms-md6"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                columnGap: 16,
              }}
            >
              <div
                style={{
                  width: 96,
                  padding: "12px 16px",
                  textAlign: "center",
                  background: modality === 0 ? "#FEC40D" : "#fff",
                  borderColor: modality === 0 ? "#FEC40D" : "#fff",
                  borderWidth: 0.5,
                  borderRadius: 4,
                  cursor: modality === 0 ? "default" : "pointer",
                }}
                onClick={() => {
                  setNextPage(1);
                  setModality(0);
                  loadItemsAsync(1, keyword === undefined ? "" : keyword, 0);
                }}
              >
                Marine
              </div>

              <div
                style={{
                  width: 96,
                  padding: "12px 16px",
                  textAlign: "center",
                  background: modality === 1 ? "#FEC40D" : "#fff",
                  borderColor: modality === 1 ? "#FEC40D" : "#fff",
                  borderWidth: 0.5,
                  borderRadius: 4,
                  cursor: modality === 1 ? "default" : "pointer",
                }}
                onClick={() => {
                  setNextPage(1);
                  setModality(1);
                  loadItemsAsync(1, keyword === undefined ? "" : keyword, 1);
                }}
              >
                Truck
              </div>

              <div
                style={{
                  width: 96,
                  padding: "12px 16px",
                  textAlign: "center",
                  background: modality === 2 ? "#FEC40D" : "#fff",
                  borderColor: modality === 2 ? "#FEC40D" : "#fff",
                  borderWidth: 0.5,
                  borderRadius: 4,
                  cursor: modality === 2 ? "default" : "pointer",
                }}
                onClick={() => {
                  setNextPage(1);
                  setModality(2);
                  loadItemsAsync(1, keyword === undefined ? "" : keyword, 2);
                }}
              >
                Rail
              </div>
            </div>
          </div>
          <div className="ms-Grid-row" style={{ padding: "2px 20px" }}>
            <div className="ms-Grid-col ms-md6">
              <div style={{ maxWidth: 400 }}>
                <TextField
                  id="txt-search"
                  placeholder="Search by keyword"
                  iconProps={iconProps}
                  disabled={isLoading}
                  onChange={(e, newValue?: string | undefined) =>
                    setKeyword(newValue === undefined ? "" : newValue)
                  }
                  styles={{
                    fieldGroup: { height: "42px", border: "1px solid #D8DFE6" },
                    icon: {
                      lineHeight: "36px",
                      height: "40px",
                      width: "39px",
                      textAlign: "center",
                      background: "#EDEDED",
                      bottom: "0",
                      right: "0",
                      color: "#4C4C4C",
                    },
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className="ms-Grid-row"
            style={{
              padding: "20px 12px",
              fontWeight: "bold",
              margin: 0,
              color: "#7C7777",
            }}
          >
            {/* Marine */}
            {modality === 0 && (
              <>
                <div className="ms-Grid-col ms-md3">Vessel</div>
                <div className="ms-Grid-col ms-md2">Line Handling #</div>
                <div className="ms-Grid-col ms-md2">Departure Date</div>
                <div
                  className="ms-Grid-col ms-md2"
                  style={{ paddingLeft: "40px" }}
                >
                  Nominations
                </div>

                <div className="ms-Grid-col ms-md2">Status</div>
                <div className="ms-Grid-col ms-md1"></div>
              </>
            )}
            {/* Truck */}
            {modality === 1 && (
              <>
                <div className="ms-Grid-col ms-md2">Tank</div>
                <div className="ms-Grid-col ms-md1">Customer Order #</div>
                <div className="ms-Grid-col ms-md2">Loading Spot</div>
                <div
                  className="ms-Grid-col ms-md2"
                  style={{ paddingLeft: "40px" }}
                >
                  Nominations #
                </div>
                <div className="ms-Grid-col ms-md2">Status</div>
                <div className="ms-Grid-col ms-md2">Date</div>
                <div className="ms-Grid-col ms-md1"></div>
              </>
            )}
            {/* Rail */}
            {modality === 2 && (
              <>
                <div className="ms-Grid-col ms-md2">Tank</div>
                <div className="ms-Grid-col ms-md1">Rail Car #</div>
                <div className="ms-Grid-col ms-md2">Loading Spot</div>
                <div
                  className="ms-Grid-col ms-md2"
                  style={{ paddingLeft: "40px" }}
                >
                  Nominations #
                </div>
                {/* <div className="ms-Grid-col ms-md2">Status</div> */}
                <div className="ms-Grid-col ms-md2">Date</div>
                <div className="ms-Grid-col ms-md1"></div>
              </>
            )}
          </div>
          <div
            id="div-nomination-list"
            className={clsx(classes.mainList, "custom-scroll")}
          >
            <div
              id="main-scroll"
              dir="ltr"
              style={{ margin: "0 8px", paddingBottom: 55 }}
            >
              {errorMessage && (
                <MessageBar messageBarType={MessageBarType.error}>
                  {errorMessage}{" "}
                  <a
                    onClick={() =>
                      loadItemsAsync(
                        nextPage,
                        keyword === undefined ? "" : keyword
                      )
                    }
                  >
                    Click here to retry.
                  </a>
                </MessageBar>
              )}

              {dailyReports.map((item, index) => {
                return (
                  <div
                    key={`line-handling-${item.lineHandlingNo}-${index}`}
                    className="ms-Grid-row"
                  >
                    <div className="ms-Grid-col ms-md12 list-item-container">
                      {/* Marine */}
                      {modality === 0 && (
                        <ReportItem
                          dailyReport={item}
                          key={`marine-${item.lineHandlingNo}-${index}`}
                          setSelectedFileUrl={setSelectedFileUrl}
                          setSelectedFileName={setSelectedFileName}
                          setPdfContentBlob={setPdfContentBlob}
                        />
                      )}

                      {/* Truck and Rail */}
                      {[1, 2].includes(modality) && (
                        <TruckReportItem
                          dailyReport={item}
                          key={`truck-${item.lineHandlingNo}-${index}-${modality}`}
                          setSelectedFileUrl={setSelectedFileUrl}
                          setSelectedFileName={setSelectedFileName}
                          setPdfContentBlob={setPdfContentBlob}
                          setIdentifier={setIdentifier}
                          modality={modality}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              {isEndReached && (
                <div className="ms-Grid-row">
                  <div className="ms-Grid-col ms-md12">
                    <Text
                      variant="small"
                      block
                      style={{ textAlign: "center", color: "#c4c4c4" }}
                    >
                      - End of list -
                    </Text>
                  </div>
                </div>
              )}
              {isLoading && <ApiSpinner />}
            </div>
          </div>
        </div>
        <Modal showModalHeader={true} id="preview" title={selectedFileName}>
          <div style={{ height: "85dvh", width: "95dvw" }}>
            <div className={classes.formContainer}>
              <div className={classes.formHeader}>
                <div style={{ flexGrow: 1 }}></div>
                <div style={{ width: 500 }}>
                  <CreatableSelect
                    components={components}
                    inputValue={inputValue}
                    isClearable
                    isMulti
                    menuIsOpen={false}
                    onChange={(email: any) => setEmailAddresses(email)}
                    onInputChange={(email) => setInputValue(email)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter email address..."
                    value={emailAddresses}
                    isDisabled={emailStatus === "sending" ? true : false}
                  />
                  <div
                    //className={classes.modalComponent}
                    style={{
                      color: "red",
                      position: "absolute",
                      fontSize: "smaller",
                    }}
                  >
                    {emailErrorMessage}
                  </div>
                </div>
                {emailStatus === "default" && (
                  <button
                    type="submit"
                    onClick={SendEmail}
                    className={classes.sendButton}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5em",
                      }}
                    >
                      <div style={{ position: "relative", top: "-1px" }}>
                        <svg
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="m12.815 12.197-7.532 1.256a.5.5 0 0 0-.386.318L2.3 20.728c-.248.64.421 1.25 1.035.943l18-9a.75.75 0 0 0 0-1.342l-18-9c-.614-.307-1.283.304-1.035.943l2.598 6.957a.5.5 0 0 0 .386.319l7.532 1.255a.2.2 0 0 1 0 .394Z"
                            fill="#fff"
                          />
                        </svg>
                      </div>
                      <div>Send</div>
                    </div>
                  </button>
                )}
                {emailStatus === "success" && (
                  <button
                    type="submit"
                    className={classes.sendButton}
                    onClick={closeModal}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5em",
                      }}
                    >
                      <div style={{ position: "relative", top: "-1px" }}>
                        <svg
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.733 19.79a.75.75 0 0 0 1.034-1.086L5.516 12.75H20.25a.75.75 0 0 0 0-1.5H5.516l6.251-5.955a.75.75 0 0 0-1.034-1.086l-7.42 7.067a.995.995 0 0 0-.3.58.754.754 0 0 0 .001.289.995.995 0 0 0 .3.579l7.419 7.067Z"
                            fill="#fff"
                          />
                        </svg>
                      </div>
                      <div>Back to List</div>
                    </div>
                  </button>
                )}
                {emailStatus === "sending" && (
                  <button
                    type="submit"
                    className={classes.sendButton}
                    disabled={true}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5em",
                      }}
                    >
                      <div style={{ position: "relative", top: "-1px" }}>
                        <svg
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 4.5a7.5 7.5 0 1 0 7.419 6.392c-.067-.454.265-.892.724-.892.37 0 .696.256.752.623A9 9 0 1 1 18 5.292V4.25a.75.75 0 0 1 1.5 0v3a.75.75 0 0 1-.75.75h-3a.75.75 0 0 1 0-1.5h1.35a7.474 7.474 0 0 0-5.1-2Z"
                            fill="#fff"
                          />
                        </svg>
                      </div>
                      <div>Sending...</div>
                    </div>
                  </button>
                )}
                {emailStatus === "error" && (
                  <button
                    type="submit"
                    className={classes.errorButton}
                    onClick={SendEmail}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5em",
                      }}
                    >
                      <div style={{ position: "relative", top: "-1px" }}>
                        <svg
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M23 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0ZM17.5 3a.5.5 0 0 0-.5.5v4a.5.5 0 0 0 1 0v-4a.5.5 0 0 0-.5-.5Zm0 7.125a.625.625 0 1 0 0-1.25.625.625 0 0 0 0 1.25Zm0 2.875a6.478 6.478 0 0 0 4.5-1.81v5.56a3.25 3.25 0 0 1-3.066 3.245L18.75 20H5.25a3.25 3.25 0 0 1-3.245-3.066L2 16.75V8.608l9.652 5.056a.75.75 0 0 0 .696 0l2.417-1.266A6.477 6.477 0 0 0 17.5 13ZM5.25 4h6.248A6.479 6.479 0 0 0 11 6.5c0 1.993.897 3.776 2.308 4.968L12 12.153l-9.984-5.23a3.25 3.25 0 0 1 3.048-2.918L5.25 4Z"
                            fill="#fff"
                          />
                        </svg>
                      </div>
                      <div>Send</div>
                    </div>
                  </button>
                )}

                <div className={classes.toolbarSeparator}></div>
                <button className={classes.viewButton} onClick={onDownload}>
                  <svg
                    width="15"
                    height="14"
                    viewBox="0 0 15 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.00009 1.99999C7.20444 1.99999 6.44137 2.31606 5.87877 2.87867C5.31616 3.44128 5.00009 4.20434 5.00009 4.99999C5.00009 5.1326 4.94741 5.25978 4.85364 5.35354C4.75987 5.44731 4.63269 5.49999 4.50009 5.49999H4.25009C3.65335 5.49999 3.08105 5.73704 2.6591 6.159C2.23714 6.58096 2.00009 7.15325 2.00009 7.74999C2.00009 8.34673 2.23714 8.91902 2.6591 9.34098C3.08105 9.76294 3.65335 9.99999 4.25009 9.99999H5.02209C5.05274 10.3383 5.11467 10.6731 5.20709 11H4.25009C3.40599 11.0018 2.59431 10.6751 1.9868 10.0891C1.37929 9.50306 1.02361 8.70366 0.99503 7.86005C0.966446 7.01644 1.2672 6.19481 1.83365 5.569C2.4001 4.9432 3.18781 4.56233 4.03009 4.50699C4.14579 3.56592 4.59203 2.69655 5.2892 2.05394C5.98637 1.41133 6.88915 1.03727 7.83651 0.99847C8.78388 0.959672 9.71422 1.25866 10.4616 1.84211C11.209 2.42556 11.7248 3.25553 11.9171 4.18399C11.5625 4.08992 11.1993 4.03163 10.8331 4.00999C10.6276 3.42256 10.2445 2.91356 9.73696 2.55344C9.22939 2.19333 8.62242 1.99992 8.00009 1.99999ZM15.0001 9.49999C15.0001 10.0909 14.8837 10.6761 14.6575 11.2221C14.4314 11.768 14.0999 12.2641 13.6821 12.682C13.2642 13.0998 12.7681 13.4313 12.2222 13.6574C11.6762 13.8836 11.091 14 10.5001 14C9.90914 14 9.32398 13.8836 8.77801 13.6574C8.23205 13.4313 7.73597 13.0998 7.31811 12.682C6.90024 12.2641 6.56877 11.768 6.34263 11.2221C6.11648 10.6761 6.00009 10.0909 6.00009 9.49999C6.00009 8.30652 6.47419 7.16192 7.31811 6.31801C8.16202 5.4741 9.30661 4.99999 10.5001 4.99999C11.6936 4.99999 12.8382 5.4741 13.6821 6.31801C14.526 7.16192 15.0001 8.30652 15.0001 9.49999ZM10.1461 11.853L10.1491 11.856C10.2418 11.9476 10.3667 11.9993 10.4971 12H10.5031C10.6344 11.9992 10.7601 11.9467 10.8531 11.854L12.8531 9.85399C12.947 9.76024 12.9998 9.63303 12.9999 9.50034C13 9.36766 12.9473 9.24038 12.8536 9.14649C12.7598 9.0526 12.6326 8.99981 12.4999 8.99971C12.3673 8.99962 12.24 9.05224 12.1461 9.14599L11.0001 10.293V7.49999C11.0001 7.36738 10.9474 7.2402 10.8536 7.14644C10.7599 7.05267 10.6327 6.99999 10.5001 6.99999C10.3675 6.99999 10.2403 7.05267 10.1465 7.14644C10.0528 7.2402 10.0001 7.36738 10.0001 7.49999V10.293L8.85409 9.14599C8.7602 9.0521 8.63286 8.99936 8.50009 8.99936C8.36731 8.99936 8.23997 9.0521 8.14609 9.14599C8.0522 9.23988 7.99945 9.36722 7.99945 9.49999C7.99945 9.63277 8.0522 9.7601 8.14609 9.85399L10.1461 11.854V11.853Z"
                      fill="#4C4C4C"
                    />
                  </svg>
                </button>
                <button
                  id="btn-print"
                  type="button"
                  className={classes.viewButton}
                  onClick={onPrint}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="12"
                    viewBox="0 0 14 12"
                    fill="none"
                  >
                    <path
                      d="M3 1.5C3 1.10218 3.15804 0.720644 3.43934 0.43934C3.72064 0.158035 4.10218 0 4.5 0H9.5C9.89782 0 10.2794 0.158035 10.5607 0.43934C10.842 0.720644 11 1.10218 11 1.5V2H12C12.5304 2 13.0391 2.21071 13.4142 2.58579C13.7893 2.96086 14 3.46957 14 4V8.5C14 8.89782 13.842 9.27936 13.5607 9.56066C13.2794 9.84196 12.8978 10 12.5 10H11V10.5C11 10.8978 10.842 11.2794 10.5607 11.5607C10.2794 11.842 9.89782 12 9.5 12H4.5C4.10218 12 3.72064 11.842 3.43934 11.5607C3.15804 11.2794 3 10.8978 3 10.5V10H1.5C1.10218 10 0.720644 9.84196 0.43934 9.56066C0.158035 9.27936 0 8.89782 0 8.5V4C0 3.46957 0.210714 2.96086 0.585786 2.58579C0.960859 2.21071 1.46957 2 2 2H3V1.5ZM3 9V8.5C3 8.10218 3.15804 7.72064 3.43934 7.43934C3.72064 7.15804 4.10218 7 4.5 7H9.5C9.89782 7 10.2794 7.15804 10.5607 7.43934C10.842 7.72064 11 8.10218 11 8.5V9H12.5C12.6326 9 12.7598 8.94732 12.8536 8.85355C12.9473 8.75979 13 8.63261 13 8.5V4C13 3.73478 12.8946 3.48043 12.7071 3.29289C12.5196 3.10536 12.2652 3 12 3H2C1.73478 3 1.48043 3.10536 1.29289 3.29289C1.10536 3.48043 1 3.73478 1 4V8.5C1 8.63261 1.05268 8.75979 1.14645 8.85355C1.24021 8.94732 1.36739 9 1.5 9H3ZM4 2H10V1.5C10 1.36739 9.94732 1.24021 9.85355 1.14645C9.75979 1.05268 9.63261 1 9.5 1H4.5C4.36739 1 4.24021 1.05268 4.14645 1.14645C4.05268 1.24021 4 1.36739 4 1.5V2ZM4 8.5V10.5C4 10.6326 4.05268 10.7598 4.14645 10.8536C4.24021 10.9473 4.36739 11 4.5 11H9.5C9.63261 11 9.75979 10.9473 9.85355 10.8536C9.94732 10.7598 10 10.6326 10 10.5V8.5C10 8.36739 9.94732 8.24021 9.85355 8.14645C9.75979 8.05268 9.63261 8 9.5 8H4.5C4.36739 8 4.24021 8.05268 4.14645 8.14645C4.05268 8.24021 4 8.36739 4 8.5Z"
                      fill="#4C4C4C"
                    />
                  </svg>
                </button>
              </div>
              <div
                className={clsx(classes.printContainer, "custom-scroll")}
                style={{ paddingTop: 20 }}
              >
                {IsNullOrEmpty(selectedFileName) ? (
                  <div style={{ width: "100%" }}>
                    <Loader label="Loading..."></Loader>
                  </div>
                ) : (
                  <iframe
                    ref={pageRef}
                    id="print-window"
                    scrolling="no"
                    frameBorder={0}
                    style={{
                      position: "relative",
                      height: "100%",
                      width: "100%",
                    }}
                    src={`${selectedFileUrl}#toolbar=0&zoom=100`}
                  ></iframe>
                )}
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default connect(
  (state: State) => state.general,
  GeneralStore.actionCreators
)(ReportPage as any);
